/**
 * SEO Helper Utilities
 * ====================
 * Utility functions for managing SEO elements dynamically.
 */

/**
 * Updates the document title
 * @param {string} title - The page title
 */
export const updateDocumentTitle = (title) => {
  document.title = title;
};

/**
 * Updates or creates a meta tag
 * @param {string} name - The meta tag name or property
 * @param {string} content - The content value
 * @param {boolean} isProperty - Whether to use 'property' instead of 'name' attribute
 */
export const updateMetaTag = (name, content, isProperty = false) => {
  const attribute = isProperty ? 'property' : 'name';
  let meta = document.querySelector(`meta[${attribute}="${name}"]`);

  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, name);
    document.head.appendChild(meta);
  }

  meta.setAttribute('content', content);
};

/**
 * Updates the canonical URL
 * @param {string} url - The canonical URL
 */
export const updateCanonicalUrl = (url) => {
  let canonical = document.querySelector('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }

  canonical.setAttribute('href', url);
};

/**
 * Injects JSON-LD structured data into the page
 * @param {Object|Array} data - The structured data object(s)
 * @param {string} id - Unique identifier for the script tag
 */
export const injectStructuredData = (data, id = 'structured-data') => {
  // Remove existing script with same ID
  const existing = document.getElementById(id);
  if (existing) {
    existing.remove();
  }

  const script = document.createElement('script');
  script.id = id;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

/**
 * Injects multiple JSON-LD structured data scripts
 * @param {Array} dataArray - Array of { data, id } objects
 */
export const injectMultipleStructuredData = (dataArray) => {
  dataArray.forEach(({ data, id }) => {
    injectStructuredData(data, id);
  });
};

/**
 * Removes structured data script by ID
 * @param {string} id - The script ID to remove
 */
export const removeStructuredData = (id) => {
  const script = document.getElementById(id);
  if (script) {
    script.remove();
  }
};

/**
 * Updates all SEO meta tags for a page
 * @param {Object} seoData - Object containing all SEO data
 */
export const updatePageSEO = (seoData) => {
  const {
    title,
    description,
    keywords,
    canonical,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl
  } = seoData;

  // Update title
  if (title) updateDocumentTitle(title);

  // Update standard meta tags
  if (description) updateMetaTag('description', description);
  if (keywords) updateMetaTag('keywords', keywords);

  // Update Open Graph tags
  if (ogTitle) updateMetaTag('og:title', ogTitle, true);
  if (ogDescription) updateMetaTag('og:description', ogDescription, true);
  if (ogImage) updateMetaTag('og:image', ogImage, true);
  if (ogUrl) updateMetaTag('og:url', ogUrl, true);
  updateMetaTag('og:type', 'website', true);
  updateMetaTag('og:locale', 'en_GB', true);

  // Update Twitter Card tags
  updateMetaTag('twitter:card', 'summary_large_image');
  if (ogTitle) updateMetaTag('twitter:title', ogTitle);
  if (ogDescription) updateMetaTag('twitter:description', ogDescription);
  if (ogImage) updateMetaTag('twitter:image', ogImage);

  // Update canonical URL
  if (canonical) updateCanonicalUrl(canonical);
};

/**
 * Generates breadcrumb items for a page
 * @param {string} pageName - The current page name
 * @param {string} pageUrl - The current page URL
 * @param {string} baseUrl - The base URL of the website
 * @returns {Array} Array of breadcrumb items
 */
export const generateBreadcrumbs = (pageName, pageUrl, baseUrl) => {
  const breadcrumbs = [
    { name: 'Home', url: baseUrl }
  ];

  if (pageName !== 'Home') {
    breadcrumbs.push({ name: pageName, url: pageUrl });
  }

  return breadcrumbs;
};

/**
 * Formats a phone number for tel: links
 * @param {string} phone - The phone number
 * @returns {string} Formatted phone number for tel: href
 */
export const formatPhoneForLink = (phone) => {
  return phone.replace(/\s/g, '').replace(/[()-]/g, '');
};

/**
 * Creates a mailto link
 * @param {string} email - The email address
 * @param {string} subject - Optional email subject
 * @returns {string} Formatted mailto link
 */
export const createMailtoLink = (email, subject = '') => {
  const baseLink = `mailto:${email}`;
  return subject ? `${baseLink}?subject=${encodeURIComponent(subject)}` : baseLink;
};

/**
 * Scroll to top of page smoothly
 */
export const scrollToTop = () => {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

/**
 * Check if element is in viewport
 * @param {HTMLElement} element - The element to check
 * @returns {boolean} Whether element is in viewport
 */
export const isInViewport = (element) => {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

/**
 * Generates alt text for food images
 * @param {string} itemName - Name of the food item
 * @param {string} category - Category of the item
 * @returns {string} SEO-optimized alt text
 */
export const generateFoodAltText = (itemName, category = '') => {
  const location = 'Gup Shup Romford';
  if (category) {
    return `${itemName} - ${category} at ${location}`;
  }
  return `${itemName} at ${location}`;
};

/**
 * Preload critical images
 * @param {Array} imageSrcs - Array of image URLs to preload
 */
export const preloadImages = (imageSrcs) => {
  imageSrcs.forEach(src => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    document.head.appendChild(link);
  });
};
